import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag } from "lucide-react";
import http from "@/api";
import { useApp } from "@/context";
import { BnplBadge } from "@/components/BnplBadge";

export default function Cart() {
  const { cart, updateQty, removeFromCart, formatPrice, t, lang, user, country } = useApp();
  const nav = useNavigate();

  const totalSar = cart.reduce((a, i) => a + (i._snapshot?.price_sar || 0) * i.quantity, 0);
  // 🧾 الإجمالي المستحق فعلياً (شامل الضريبة إن وُجدت) — تمريره لرسائل التقسيط الرسمية
  // (مرآة حساب الشيك آوت — الخادم يبقى مصدر الحقيقة عند إنشاء الطلب)
  const [taxCfg, setTaxCfg] = useState(null);
  useEffect(() => { http.get("/tax-config").then((r) => setTaxCfg(r.data || null)).catch(() => {}); }, []);
  const cc = String(country || "").toUpperCase();
  const taxConf = (taxCfg && taxCfg[cc]) || null;
  const vatRate = taxConf != null ? (Number(taxConf.vat_rate) || 0) : (cc === "SA" ? 0.15 : 0);
  const cartVatSar = (vatRate > 0 && !taxConf?.prices_include_vat)
    ? Math.round(totalSar * vatRate * 100) / 100 : 0;
  const payableSar = (() => {
    const g = Math.round((totalSar + cartVatSar) * 100) / 100;
    return taxConf?.rounding === "integer" ? Math.round(g) : g;
  })();
  const totalSavings = cart.reduce((a, i) => {
    const orig = i._snapshot?.original_price_sar || 0;
    const base = i._snapshot?.base_price_sar || 0;
    return a + Math.max(0, orig - base) * i.quantity;
  }, 0);

  if (cart.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <ShoppingBag size={48} className="mx-auto opacity-30 mb-4" />
        <h2 className={`${lang === "ar" ? "font-display-ar" : "font-display-en"} text-xl`}>{t("emptyCart")}</h2>
        <p className="opacity-60 mt-2">{t("emptyCartHint")}</p>
        <Link to="/shop" className="btn-primary mt-5 inline-flex" data-testid="empty-cart-shop-btn">{t("startShopping")}</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 space-y-4" data-testid="cart-page">
      <h1 className={`${lang === "ar" ? "font-display-ar" : "font-display-en"} text-xl`}>{t("cart")}</h1>
      <div className="space-y-3">
        {cart.map((i, idx) => {
          const key = `${i.product_id}__${i.variant_id || ""}__${idx}`;
          const tid = `${i.product_id}${i.variant_id ? "-" + i.variant_id : ""}`;
          const optsLabels = i._snapshot?.options_labels || [];
          return (
          <div key={key} className="card-soft p-3 flex items-start gap-3" data-testid={`cart-item-${tid}`}>
            <img src={i._snapshot?.image} className="w-20 h-20 rounded-xl object-cover shrink-0" alt="" />
            <div className="flex-1 min-w-0">
              <div className="font-medium line-clamp-1">
                {lang === "ar" ? i._snapshot?.name_ar : i._snapshot?.name_en}
              </div>
              {i._snapshot?.variant_name && (
                <div className="text-xs opacity-60 mt-0.5">
                  {lang === "ar" ? "الخيار" : "Option"}: {i._snapshot.variant_name}
                </div>
              )}
              {/* iter#36: show selected options breakdown */}
              {optsLabels.length > 0 && (
                <div className="mt-1 space-y-0.5" data-testid={`cart-opts-${tid}`}>
                  {optsLabels.map((o, oi) => (
                    <div key={oi} className="text-[11px] opacity-70 flex items-center gap-1.5">
                      <span className="opacity-60">{lang === "ar" ? o.group_title_ar : o.group_title_en}:</span>
                      <span className="font-medium">{lang === "ar" ? o.label_ar : o.label_en}</span>
                      {o.price_delta_sar > 0 && (
                        <span className="opacity-60">(+{formatPrice(o.price_delta_sar)})</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="price-display mt-1 flex items-center gap-2">
                <span>{formatPrice(i._snapshot?.price_sar)}</span>
                {(i._snapshot?.original_price_sar || 0) > (i._snapshot?.base_price_sar || 0) && (
                  <span className="text-xs opacity-50 line-through font-normal" data-testid={`cart-orig-${tid}`}>
                    {formatPrice((i._snapshot.original_price_sar || 0) + (i._snapshot.options_delta_sar || 0))}
                  </span>
                )}
              </div>
              <div className="mt-2 flex items-center gap-1 border rounded-full p-0.5 w-fit" style={{ borderColor: "var(--border)" }}>
                <button className="w-7 h-7 rounded-full hover:bg-black/5 flex items-center justify-center"
                        onClick={() => updateQty(i.product_id, i.quantity - 1, i.variant_id, i.selected_options)} data-testid={`cart-dec-${tid}`}>
                  <Minus size={12} />
                </button>
                <div className="w-7 text-center text-sm">{i.quantity}</div>
                <button className="w-7 h-7 rounded-full hover:bg-black/5 flex items-center justify-center"
                        onClick={() => updateQty(i.product_id, i.quantity + 1, i.variant_id, i.selected_options)} data-testid={`cart-inc-${tid}`}>
                  <Plus size={12} />
                </button>
              </div>
            </div>
            <button onClick={() => removeFromCart(i.product_id, i.variant_id, i.selected_options)}
                    className="p-2 rounded-full hover:bg-red-50 text-red-500" data-testid={`cart-remove-${tid}`}>
              <Trash2 size={16} />
            </button>
          </div>
          );
        })}
      </div>

      <div className="card-soft p-4 mt-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm opacity-60">{t("total")}</div>
            <div className="price-display text-xl">{formatPrice(totalSar)}</div>
            {cartVatSar > 0 && (
              <div className="text-[11px] font-semibold opacity-70 mt-0.5" data-testid="cart-vat-incl-total">
                {lang === "ar"
                  ? `الإجمالي شامل الضريبة (${Math.round(vatRate * 100)}%): ${formatPrice(payableSar)}`
                  : `Total incl. VAT (${Math.round(vatRate * 100)}%): ${formatPrice(payableSar)}`}
              </div>
            )}
            {totalSavings > 0 && (
              <div className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full" data-testid="cart-savings-badge">
                🎉 {lang === "ar" ? `وفّرت ${formatPrice(totalSavings)}` : `You saved ${formatPrice(totalSavings)}`}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-1">
            <button className="btn-primary" onClick={() => nav("/checkout")} data-testid="cart-checkout-btn">
              {t("checkout")}
            </button>
            {!user && (
              <div className="text-[11px] opacity-60 flex items-center gap-1" data-testid="guest-hint">
                🔓 {lang === "ar" ? "يمكنك الطلب كزائر بدون تسجيل" : "Guest checkout — no sign-up needed"}
              </div>
            )}
          </div>
        </div>
        {/* 💳 رسائل التقسيط بالسلة — سنيبت Tabby الرسمي بالمبلغ المستحق شاملاً الضريبة */}
        <BnplBadge price={payableSar} source="cart" includesVat />
      </div>
    </div>
  );
}
